import type { AuditLogEntry } from "api/auditLogs";
import type { StatusTone } from "Components/ui";
import type { ActionKind } from "../types";

// Short words kept upper-case in action labels ("USER_RESET_OTP" → "User reset OTP").
const ACRONYMS = new Set([
  "2FA",
  "API",
  "CSV",
  "DB",
  "ID",
  "IP",
  "OTP",
  "RBAC",
  "SAP",
  "URI",
  "URL",
]);

/** "TENANT_STATUS_UPDATED" → "Tenant status updated"; camelCase and dotted names work too. */
export const formatActionLabel = (action?: string): string => {
  if (!action) {
    return "Unknown action";
  }
  const words = action
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(/[\s_.-]+/)
    .filter(Boolean)
    .map((word) => (ACRONYMS.has(word.toUpperCase()) ? word.toUpperCase() : word.toLowerCase()));
  if (words.length === 0) {
    return action;
  }
  const [first, ...rest] = words;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(" ");
};

/**
 * Tone family of an action name. Create / delete / update use the same tokens as the action-type
 * filter, so a badge's color always matches the filter that finds it.
 */
export const getActionKind = (action?: string): ActionKind => {
  const name = (action || "").toUpperCase();
  // Money coming in: a payment received or recorded, and its receipt.
  if (/^BILLING_PAYMENT_(RECEIVED|RECORDED|RECEIPT_ISSUED)$/.test(name)) {
    return "payment";
  }
  if (/CREATE|ADD/.test(name)) {
    return "create";
  }
  if (/DELETE|REMOVE/.test(name)) {
    return "delete";
  }
  if (/UPDATE|EDIT|CHANGE/.test(name)) {
    return "update";
  }
  if (/REVEAL|ROTATE|BLOCK|REVERS|VOID|OVERRIDE|MISMATCH/.test(name)) {
    return "sensitive";
  }
  if (/LOGIN|LOGOUT|PASSWORD|OTP|2FA|TWO_FACTOR|SESSION/.test(name)) {
    return "auth";
  }
  return "other";
};

export const ACTION_KIND_META: Record<ActionKind, { tone: StatusTone; label: string }> = {
  create: { tone: "success", label: "Create" },
  update: { tone: "info", label: "Update" },
  delete: { tone: "danger", label: "Delete" },
  payment: { tone: "success", label: "Payment" },
  sensitive: { tone: "warning", label: "Sensitive" },
  auth: { tone: "neutral", label: "Sign-in" },
  other: { tone: "neutral", label: "Other" },
};

export const getActionMeta = (action?: string) => ACTION_KIND_META[getActionKind(action)];

/** Entries no admin made (gateway payments, receipts, the nightly reconcile). */
export const isSystemEntry = (entry: Pick<AuditLogEntry, "actorType">): boolean =>
  entry.actorType === "system";

const SOURCE_LABELS: Record<string, string> = {
  webhook: "Razorpay webhook",
  pay_return: "Pay page, after paying",
  link_recheck: "Payment link re-check",
  nightly_reconcile: "Nightly reconcile",
  backfill: "Added later from payment records",
  gateway: "Payment gateway",
};

/** Where a system entry came from, in words ("webhook" → "Razorpay webhook"). */
export const formatSource = (source?: string | null): string =>
  (source && SOURCE_LABELS[source]) || source || "Automatic";

/** Paise fields in billing snapshots, shown in rupees next to the stored value. */
export const MONEY_FIELDS = new Set([
  "amount",
  "amountPaid",
  "balance",
  "expected",
  "paymentTotal",
  "subtotal",
  "total",
  "unitPrice",
]);
